import "./Stepper.css";

import { Fragment } from "react";

import { CircleFilledIcon, CompletedCircleGreenIcon, ErrorIcon } from "../../../assets";
import { ColorConstants, OrganisationTypeConstants } from "../../../constants";
import { StepItem, StepState } from "../../../models";

interface StepperProps {
  steps: StepItem[];
  currentSubStepKey?: string;
  onClick?: (stepKey: string) => void;
  currentUserType: OrganisationTypeConstants;
  // Opt-in, so the activity wizards - which also run as Developer - keep the green treatment.
  variant?: "green" | "indigo";
  hideDividers?: boolean;
}

export const Stepper = ({
  steps,
  currentSubStepKey,
  onClick,
  currentUserType,
  variant = "green",
  hideDividers = false,
}: StepperProps): JSX.Element => {
  const isIndigo = variant === "indigo";
  const inProgressFill = isIndigo ? ColorConstants.INDIGO_500 : ColorConstants.GREEN_500;
  // Indigo journeys de-emphasise finished steps back to grey, so only the current one is coloured.
  const renderComplete = (index: number): JSX.Element =>
    isIndigo ? (
      <>
        <CircleFilledIcon width="24" height="24" fill={ColorConstants.GREY_100} />
        <div className="StepNumber">{index + 1}</div>
      </>
    ) : (
      <CompletedCircleGreenIcon />
    );

  return (
    <div className={`Stepper ${isIndigo ? "Stepper_indigo" : ""}`}>
      {steps.map((step, i) => {
        if (onClick) {
          return (
            <Fragment key={step.stepName}>
              {i > 0 && !hideDividers && (
                <div className={`StepDivider ${StepState[step.stepState]} ${currentUserType}`} />
              )}
              <div
                role="button"
                onKeyDown={(e) => e.key === "Enter" && onClick(step.key)}
                onClick={() => onClick(step.key)}
                className={`StepperStep ${StepState[step.stepState]} ${
                  currentSubStepKey === step.key ? "IsCurrent" : ""
                }`}
                tabIndex={0}
              >
                <div className="StepIcon">
                  {step.stepState === StepState.NotStarted && (
                    <>
                      <CircleFilledIcon width="24" height="24" fill={ColorConstants.GREY_100} />
                      <div className="StepNumber">{i + 1}</div>
                    </>
                  )}
                  {step.stepState === StepState.InProgress && (
                    <>
                      <CircleFilledIcon width="24" height="24" fill={inProgressFill} />
                      <div className="StepNumber">{i + 1}</div>
                    </>
                  )}
                  {step.stepState === StepState.Invalid &&
                    (currentUserType === OrganisationTypeConstants.VERIFIER ? (
                      <>
                        <CircleFilledIcon width="24" height="24" fill={ColorConstants.GOLDENROD_600} />
                        <div className="StepNumber">{i + 1}</div>
                      </>
                    ) : (
                      <ErrorIcon fill={ColorConstants.RED_500} width={24} height={24} />
                    ))}
                  {step.stepState === StepState.Warning &&
                    (currentUserType === OrganisationTypeConstants.DEVELOPER ? (
                      <>
                        <CircleFilledIcon width="24" height="24" fill={ColorConstants.GOLDENROD_600} />
                        <div className="StepNumber">{i + 1}</div>
                      </>
                    ) : (
                      <ErrorIcon fill={ColorConstants.RED_500} width={24} height={24} />
                    ))}
                  {step.stepState === StepState.Complete && renderComplete(i)}
                </div>
                <p className="body2">{step.stepName}</p>
              </div>
            </Fragment>
          );
        }

        return (
          <Fragment key={step.stepName}>
            {i > 0 && !hideDividers && (
              <div className={`StepDivider ${StepState[step.stepState]} ${currentUserType}`} />
            )}
            <div
              className={`StepperStep ${StepState[step.stepState]} ${
                currentSubStepKey === step.key ? "IsCurrent" : ""
              }`}
            >
              <div className="StepIcon">
                {step.stepState === StepState.NotStarted && (
                  <CircleFilledIcon width="24" height="24" fill={ColorConstants.GREY_100} />
                )}
                {step.stepState === StepState.InProgress && (
                  <CircleFilledIcon width="24" height="24" fill={inProgressFill} />
                )}
                {step.stepState === StepState.Complete ? renderComplete(i) : <div className="StepNumber">{i + 1}</div>}
              </div>
              <p className="body2">{step.stepName}</p>
            </div>
          </Fragment>
        );
      })}
    </div>
  );
};
